import React, { useCallback, useEffect, useState, useContext } from 'react';
import { Loader2, AlertCircle, X, Check, RefreshCw, Trash2, Plus, Edit2 } from 'lucide-react';
import { semanticBrandingService, Attribute } from '../../services/semanticBranding';
import { ToastContext } from '../../App';
import AttributeEditModal from './AttributeEditModal';
interface AttributesStepProps {
  documentId: string;
  onPreviousStep: () => void;
  onNextStep: () => void;
}
const AttributesStep: React.FC<AttributesStepProps> = ({
  documentId,
  onPreviousStep,
  onNextStep
}) => {
  const {
    addToast
  } = useContext(ToastContext);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [suggestedAttributes, setSuggestedAttributes] = useState<string[]>([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [hasFetchedSuggestions, setHasFetchedSuggestions] = useState(false);
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentAttribute, setCurrentAttribute] = useState<string>('');
  const [isNewAttribute, setIsNewAttribute] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isEditingSuggestion, setIsEditingSuggestion] = useState(false);
  // Filter suggestions to remove already selected attributes
  useEffect(() => {
    const filtered = suggestedAttributes.filter(suggestion => !selectedAttributes.includes(suggestion));
    setFilteredSuggestions(filtered);
  }, [suggestedAttributes, selectedAttributes]);
  // Fetch attribute suggestions
  useEffect(() => {
    if (documentId) {
      fetchAttributeSuggestions();
    }
  }, [documentId]);
  // Track changes to selected attributes
  useEffect(() => {
    if (selectedAttributes.length > 0 && hasFetchedSuggestions) {
      setHasUnsavedChanges(true);
    }
  }, [selectedAttributes, hasFetchedSuggestions]);
  // Warn user before leaving with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = e => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = 'Você tem alterações não salvas. Tem certeza que deseja sair?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);
  const fetchAttributeSuggestions = async () => {
    setIsLoadingSuggestions(true);
    setError('');
    try {
      console.log('Fetching attribute suggestions for document:', documentId);
      const response = await semanticBrandingService.suggestAttributes(documentId);
      console.log('Attribute suggestions response:', response);
      // Check if we have attributes in the response
      if (response.atributos && Array.isArray(response.atributos)) {
        setSuggestedAttributes(response.atributos);
        console.log('Set suggested attributes:', response.atributos);
      } else {
        console.warn('No attributes found in response:', response);
        setSuggestedAttributes([]);
      }
      // If there are already selected attributes in the document, load them
      if (response.brandDocument?.metadata?.atributosSelecionados?.length) {
        setSelectedAttributes(response.brandDocument.metadata.atributosSelecionados);
        setHasUnsavedChanges(false);
      }
      setHasFetchedSuggestions(true);
    } catch (error) {
      console.error('Failed to fetch attribute suggestions:', error);
      setError(error.response?.data?.message || 'Erro ao buscar sugestões de atributos. Tente novamente.');
    } finally {
      setIsLoadingSuggestions(false);
    }
  };
  const handleToggleAttribute = (attribute: string) => {
    // Check if attribute is already selected
    const isAlreadySelected = selectedAttributes.includes(attribute);
    if (isAlreadySelected) {
      // Remove from selected
      setSelectedAttributes(selectedAttributes.filter(attr => attr !== attribute));
    } else {
      // Add to selected if under limit
      if (selectedAttributes.length >= 10) {
        setError('Limite máximo de 10 atributos atingido.');
        return;
      }
      setSelectedAttributes([...selectedAttributes, attribute]);
    }
  };
  const handleRemoveAttribute = (index: number) => {
    const newSelectedAttributes = [...selectedAttributes];
    newSelectedAttributes.splice(index, 1);
    setSelectedAttributes(newSelectedAttributes);
  };
  const handleEditAttribute = (index: number) => {
    setCurrentAttribute(selectedAttributes[index]);
    setEditingIndex(index);
    setIsNewAttribute(false);
    setIsEditingSuggestion(false);
    setIsModalOpen(true);
  };
  const handleEditSuggestion = (attribute: string) => {
    setCurrentAttribute(attribute);
    setIsNewAttribute(false);
    setIsEditingSuggestion(true);
    setIsModalOpen(true);
  };
  const handleAddNew = () => {
    if (selectedAttributes.length >= 10) {
      setError('Limite máximo de 10 atributos atingido.');
      return;
    }
    setCurrentAttribute('');
    setIsNewAttribute(true);
    setIsEditingSuggestion(false);
    setIsModalOpen(true);
  };
  const handleSaveAttribute = (attribute: string) => {
    if (isNewAttribute) {
      // Add new attribute
      const updatedAttributes = [...selectedAttributes, attribute];
      setSelectedAttributes(updatedAttributes);
    } else if (isEditingSuggestion) {
      // Update suggestion and add to selected attributes
      const updatedSuggestions = suggestedAttributes.filter(attr => attr !== currentAttribute);
      setSuggestedAttributes(updatedSuggestions);
      if (!selectedAttributes.includes(attribute)) {
        setSelectedAttributes([...selectedAttributes, attribute]);
      }
    } else if (editingIndex !== null) {
      // Update existing attribute
      const updatedAttributes = [...selectedAttributes];
      updatedAttributes[editingIndex] = attribute;
      setSelectedAttributes(updatedAttributes);
    }
    // Close modal and reset state
    setIsModalOpen(false);
    setCurrentAttribute('');
    setEditingIndex(null);
    setIsNewAttribute(false);
    setIsEditingSuggestion(false);
  };
  const handleDeleteAll = () => {
    if (window.confirm('Tem certeza que deseja remover todos os atributos?')) {
      setSelectedAttributes([]);
    }
  };
  // Remove a suggestion from the list
  const handleRemoveSuggestion = (attributeToRemove: string) => {
    setSuggestedAttributes(suggestedAttributes.filter(attribute => attribute !== attributeToRemove));
  };
  const handleSaveAttributes = async () => {
    if (selectedAttributes.length === 0) {
      setError('Selecione pelo menos um atributo para continuar.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      await semanticBrandingService.saveAttributes(documentId, selectedAttributes);
      setHasUnsavedChanges(false);
      addToast('success', 'Atributos salvos com sucesso!');
      onNextStep();
    } catch (error) {
      console.error('Failed to save attributes:', error);
      setError(error.response?.data?.message || 'Erro ao salvar atributos.');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100" data-id="element-904">
      <h2 className="text-xl font-semibold mb-6 text-gray-900" data-id="element-905">
        Atributos para Análise
      </h2>
      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md flex items-start" data-id="element-906">
          <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-907" />
          <p data-id="element-908">{error}</p>
        </div>}

      {/* Attribute edit modal */}
      <AttributeEditModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} attribute={currentAttribute} onSave={handleSaveAttribute} isNew={isNewAttribute} data-id="element-909" />

      {/* Selected attributes */}
      <div className="mb-8" data-id="element-910">
        <div className="flex justify-between items-center mb-4" data-id="element-911">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-912">
            Atributos selecionados
          </h3>
          <div className="flex space-x-2" data-id="element-913">
            <button onClick={handleAddNew} disabled={selectedAttributes.length >= 10} className={`px-3 py-1.5 rounded-md flex items-center text-sm ${selectedAttributes.length >= 10 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-black text-white hover:bg-gray-800'}`} data-id="element-914">
              <Plus size={14} className="mr-1" data-id="element-915" />
              Adicionar
            </button>
            {selectedAttributes.length > 0 && hasFetchedSuggestions && <button onClick={handleDeleteAll} className="px-3 py-1.5 bg-red-50 text-red-600 rounded-md hover:bg-red-100 flex items-center text-sm" data-id="element-916">
                <Trash2 size={14} className="mr-1" data-id="element-917" />
                Remover todos
              </button>}
          </div>
        </div>
        {selectedAttributes.length === 0 ? <div className="p-4 bg-gray-50 rounded-md text-gray-700 text-center" data-id="element-918">
            Nenhum atributo selecionado
          </div> : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4" data-id="element-919">
            {selectedAttributes.map((attribute, index) => <div key={index} className="p-3 border border-gray-200 rounded-md relative hover:border-[#1649ff] hover:bg-blue-50 transition-colors group" data-id="element-920">
                <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-921">
                  <button onClick={() => handleEditAttribute(index)} className="p-1 text-gray-400 hover:text-[#1649ff] rounded-full" data-id="element-922">
                    <Edit2 size={14} data-id="element-923" />
                  </button>
                  <button onClick={() => handleRemoveAttribute(index)} className="p-1 text-gray-400 hover:text-red-500 rounded-full" data-id="element-924">
                    <X size={14} data-id="element-925" />
                  </button>
                </div>
                <h4 className="font-medium text-sm text-center text-gray-900 min-h-[40px] flex items-center justify-center px-4 pt-2" data-id="element-926">
                  {attribute}
                </h4>
              </div>)}
          </div>}
      </div>

      {/* Suggested attributes */}
      <div data-id="element-927">
        <div className="flex justify-between items-center mb-4" data-id="element-928">
          <h3 className="text-lg font-medium text-gray-900" data-id="element-929">
            Sugestões de atributos
          </h3>
          {!isLoadingSuggestions && hasFetchedSuggestions && <button onClick={fetchAttributeSuggestions} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 flex items-center text-sm" disabled={isLoadingSuggestions} data-id="element-930">
              <RefreshCw size={14} className={`mr-1 ${isLoadingSuggestions ? 'animate-spin' : ''}`} data-id="element-931" />
              Sugerir novos
            </button>}
        </div>
        {isLoadingSuggestions ? <div className="flex justify-center items-center py-8" data-id="element-932">
            <Loader2 size={24} className="animate-spin text-[#1649ff] mr-2" data-id="element-933" />
            <span className="text-gray-900" data-id="element-934">
              Gerando sugestões de atributos...
            </span>
          </div> : !hasFetchedSuggestions ? <div className="p-4 bg-gray-50 rounded-md text-gray-700 text-center" data-id="element-935">
            Carregando sugestões de atributos...
          </div> : filteredSuggestions.length === 0 ? <div className="p-4 bg-gray-50 rounded-md text-gray-700 text-center flex flex-col items-center" data-id="element-936">
            <p className="mb-2" data-id="element-937">Nenhuma sugestão disponível</p>
            <button onClick={fetchAttributeSuggestions} className="px-3 py-1.5 bg-[#1649ff] text-white rounded-md hover:bg-blue-700 flex items-center" data-id="element-938">
              <RefreshCw size={14} className="mr-1" data-id="element-939" />
              Gerar sugestões
            </button>
          </div> : <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4" data-id="element-940">
            {filteredSuggestions.map((attribute, index) => <div key={index} className="p-3 border border-gray-200 rounded-md cursor-pointer relative transition-colors hover:border-[#1649ff] hover:bg-blue-50 group" onClick={() => handleToggleAttribute(attribute)} data-id="element-941">
                <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-942">
                  <button onClick={e => {
              e.stopPropagation(); // Prevent card click from triggering
              handleEditSuggestion(attribute);
            }} className="p-1 text-gray-400 hover:text-[#1649ff] rounded-full" data-id="element-943">
                    <Edit2 size={14} data-id="element-944" />
                  </button>
                  <button onClick={e => {
              e.stopPropagation(); // Prevent card click from triggering
              handleRemoveSuggestion(attribute);
            }} className="p-1 text-gray-400 hover:text-red-500 rounded-full" data-id="element-945">
                    <X size={14} data-id="element-946" />
                  </button>
                </div>
                <h4 className="font-medium text-sm text-center text-gray-900 min-h-[40px] flex items-center justify-center px-4 pt-2" data-id="element-947">
                  {attribute}
                </h4>
              </div>)}
          </div>}
      </div>

      {/* Action buttons */}
      <div className="mt-8 flex justify-between" data-id="element-948">
        <button onClick={onPreviousStep} className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200" disabled={isLoading} data-id="element-949">
          Voltar
        </button>
        <button onClick={handleSaveAttributes} className="px-4 py-2 bg-[#1649ff] text-white rounded-md hover:bg-blue-700 flex items-center" disabled={isLoading || selectedAttributes.length === 0 || !hasFetchedSuggestions} data-id="element-950">
          {isLoading ? <>
              <Loader2 size={16} className="mr-2 animate-spin" data-id="element-951" />
              Salvando...
            </> : 'Continuar para Análise'}
        </button>
      </div>

      {hasUnsavedChanges && <div className="mt-4 p-3 bg-yellow-50 text-yellow-700 rounded-md flex items-start" data-id="element-952">
          <AlertCircle size={18} className="mr-2 mt-0.5 flex-shrink-0" data-id="element-953" />
          <p data-id="element-954">
            Você tem alterações não salvas. Clique em "Continuar para Análise"
            para salvá-las.
          </p>
        </div>}
    </div>;
};
export default AttributesStep;